import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  catchError,
  filter,
  retry,
  switchMap,
  take,
  throwError,
  timer,
} from 'rxjs';
import { AuthService } from './services/auth.service';
import { ResponseData } from './models/responseData.interface';

const isRefreshing$ = new BehaviorSubject<boolean>(false);
let refreshTokenSubject = new BehaviorSubject<string | null>(null);

function isAuthEndpoint(url: string): boolean {
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/logout') ||
    url.includes('/auth/recoverpass') ||
    url.includes('/auth/validaCodigo') ||
    url.includes('/auth/actualizaPass')
  );
}

function addAuthHeader(
  req: HttpRequest<unknown>,
  token: string
): HttpRequest<unknown> {
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}

function handle401Error(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router
) {
  const refreshToken = authService.getRefreshToken();

  if (!refreshToken) {
    authService.clearSession();
    router.navigate(['/login']);
    return throwError(() => 'Sesión expirada. Inicia sesión nuevamente.');
  }

  if (!isRefreshing$.value) {
    isRefreshing$.next(true);
    refreshTokenSubject.next(null);


    return authService.refreshTokens().pipe(
      retry({ count: 1, delay: () => timer(1_000) }),
      switchMap((resp: ResponseData) => {

        const data = resp?.data ?? {};
        const newAccessToken = (data.token ?? data.accessToken) as string | undefined;
        const newRefreshToken = (data.refreshToken ?? data.refresh_token) as string | undefined;

        if (!newAccessToken || (typeof newAccessToken !== 'string')) {
          isRefreshing$.next(false);
          authService.clearSession();
          router.navigate(['/login']);
          return throwError(
            () => 'No fue posible renovar la sesión. Inicia sesión nuevamente.'
          );
        }

        authService.storeToken(newAccessToken);
        if (newRefreshToken && typeof newRefreshToken === 'string') {
          authService.storeRefreshToken(newRefreshToken);
        }

        isRefreshing$.next(false);
        refreshTokenSubject.next(newAccessToken);

        const retryReq = addAuthHeader(req, newAccessToken);
        return next(retryReq);
      }),
      catchError((err) => {
        isRefreshing$.next(false);

        refreshTokenSubject = new BehaviorSubject<string | null>(null);
        authService.clearSession();
        router.navigate(['/login']);
        return throwError(() => err);
      })
    );
  }

  return refreshTokenSubject.pipe(
    filter((token): token is string => token !== null),
    take(1),
    switchMap((newAccessToken: string) => {
      const retryReq = addAuthHeader(req, newAccessToken);
      return next(retryReq);
    }),
    catchError((err) => throwError(() => err))
  );
}


export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const skip = isAuthEndpoint(req.url);
  const token = authService.getToken();

  if (token && !skip) {
    req = addAuthHeader(req, token);
  }

  return next(req).pipe(
    catchError((e: HttpErrorResponse) => {
    
      if (e.status === 401 && !skip) {
        return handle401Error(req, next, authService, router);
      }


      const error = e.error?.message || e.statusText;
      return throwError(() => error);
    })
  );
};